import type {
  AxlPilotAssessmentQuestion,
  AxlPilotAssessmentUnit,
  ReimagineAssessmentSnapshot,
} from "../types/axlPilotContext";
import type { AuthUser } from "../auth/types";
import { isLeafUnit } from "./assessmentUnitUtils";
import {
  normalizeQuestionList,
  QUESTION_STATE,
} from "./subCapabilityQuestionUtils";
import { buildFormConfigFromSubCapability } from "./subCapabilityAssessmentConfig";

function resolveMaturityOptionsJson(
  sub: Record<string, unknown>
): string | null {
  const raw = sub.maturityOptionsJson ?? sub.MaturityOptionsJson;
  if (typeof raw === "string" && raw.trim()) {
    return raw.trim();
  }

  const formConfig = buildFormConfigFromSubCapability(sub);
  if (!formConfig.maturityOptions?.length) {
    return null;
  }

  return JSON.stringify(
    formConfig.maturityOptions.map((option) => ({
      optionValue: option.optionValue,
      label: option.label.trim(),
      definition: (option.definition ?? "").trim(),
    }))
  );
}

type BuildSnapshotInput = {
  userId: number;
  projectId: number;
  authUser?: AuthUser | null;
  capabilitiesPayload?: unknown;
};

function readString(value: unknown, fallback = ""): string {
  if (value == null) return fallback;
  const text = String(value).trim();
  return text || fallback;
}

function readNumber(value: unknown): number | null {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function mapQuestions(
  list: unknown,
  stateKind: "Current" | "Future"
): AxlPilotAssessmentQuestion[] {
  return normalizeQuestionList(list, stateKind).map((question, index) => ({
    questionId: Number(question.id) > 0 ? Number(question.id) : index + 1,
    stateKind,
    questionText: question.questionText,
  }));
}

function buildAssessmentUnits(
  selectedCapabilities: unknown[]
): AxlPilotAssessmentUnit[] {
  const units: AxlPilotAssessmentUnit[] = [];
  let displayOrder = 0;

  selectedCapabilities.forEach((rawCap) => {
    const cap = rawCap as Record<string, unknown>;
    const capabilityId = Number(cap.capabilityId ?? cap.CapabilityId ?? 0);
    const capabilityName = readString(
      cap.capability ?? cap.capabilityName ?? cap.CapabilityName,
      "Capability"
    );
    const capabilityDescription = readString(
      cap.capabilityDescription ??
        cap.description ??
        cap.CapabilityDescription
    );

    const subs = (cap.selectedSubCapabilities ??
      cap.SelectedSubCapabilities ??
      []) as Record<string, unknown>[];

    subs.forEach((sub) => {
      displayOrder += 10;
      const subCapabilityIdRaw = sub.subCapabilityId ?? sub.SubCapabilityId;
      const leaf = isLeafUnit(subCapabilityIdRaw as number | null);
      const subCapabilityId = leaf
        ? null
        : readNumber(subCapabilityIdRaw) ?? null;

      const subCapabilityName = leaf
        ? null
        : readString(sub.subCapability ?? sub.subCapabilityName) || null;
      const subCapabilityDescription = leaf
        ? null
        : readString(sub.subCapabilityDescription ?? sub.description) || null;

      units.push({
        capabilityId,
        capabilityName,
        capabilityDescription,
        displayOrder,
        unitKind: leaf ? "capability_direct" : "sub_capability",
        subCapabilityId,
        subCapabilityName,
        subCapabilityDescription,
        threadSubCapabilityId: leaf ? 0 : Number(subCapabilityId) || 0,
        currentStateCommentPlaceholder: readString(
          sub.currentStateCommentPlaceholder ?? sub.CurrentStateCommentPlaceholder
        ),
        futureStateCommentPlaceholder: readString(
          sub.futureStateCommentPlaceholder ?? sub.FutureStateCommentPlaceholder
        ),
        currentState: {
          questions: mapQuestions(
            sub.currentStateQuestions ?? sub.CurrentStateQuestions,
            QUESTION_STATE.current
          ),
        },
        futureState: {
          questions: mapQuestions(
            sub.futureStateQuestions ?? sub.FutureStateQuestions,
            QUESTION_STATE.future
          ),
        },
        maturityOptionsJson: resolveMaturityOptionsJson(sub),
      });
    });
  });

  return units;
}

/**
 * Builds the assessment JSON document from data already loaded on the assessment page.
 */
export function buildReimagineAssessmentSnapshot({
  userId,
  projectId,
  authUser,
  capabilitiesPayload,
}: BuildSnapshotInput): ReimagineAssessmentSnapshot | null {
  const root = capabilitiesPayload as Record<string, unknown> | null | undefined;
  const dataNode = (root?.data ?? root) as Record<string, unknown> | undefined;
  if (!dataNode) return null;

  const selectedCapabilities = (dataNode.selectedCapabilities ??
    dataNode.SelectedCapabilities ??
    []) as unknown[];

  if (!selectedCapabilities.length) return null;

  const projectDetails = authUser?.projectDetails?.find(
    (project) => Number(project.projectId) === Number(projectId)
  );

  const blueprintRaw = (dataNode.assessmentBlueprint ??
    dataNode.AssessmentBlueprint ??
    {}) as Record<string, unknown>;

  const flatBlueprintId =
    readNumber(
      dataNode.assessmentBlueprintId ?? dataNode.AssessmentBlueprintId
    ) ?? null;
  const flatBlueprintTitle = readString(
    dataNode.assessmentBlueprintTitle ?? dataNode.AssessmentBlueprintTitle
  );

  const companyRaw = (dataNode.company ?? dataNode.Company ?? {}) as Record<
    string,
    unknown
  >;

  const projectRaw = (dataNode.project ?? dataNode.Project ?? {}) as Record<
    string,
    unknown
  >;

  const companyId =
    readNumber(companyRaw.companyId ?? companyRaw.CompanyId) ??
    readNumber(projectDetails?.companyId) ??
    null;

  const projectName =
    readString(
      projectRaw.projectName ??
        projectRaw.ProjectName ??
        projectDetails?.projectName,
      "Assessment project"
    ) || "Assessment project";

  return {
    userId: Number(userId),
    projectId: Number(projectId),
    title: readString(dataNode.title ?? dataNode.assessmentTitle, "Assessment help"),
    userType: readString(
      dataNode.userType ?? authUser?.userType ?? authUser?.roleName,
      "Client"
    ),
    company: {
      companyId,
      companyName: readString(
        companyRaw.companyName ??
          companyRaw.CompanyName ??
          dataNode.companyName ??
          dataNode.CompanyName ??
          projectDetails?.companyName
      ),
      companyInfo: readString(companyRaw.companyInfo ?? companyRaw.CompanyInfo),
      companyContact: readString(
        companyRaw.companyContact ??
          companyRaw.CompanyContact ??
          authUser?.contactInfo
      ),
      industry: readString(
        companyRaw.industry ?? companyRaw.Industry ?? authUser?.businessType
      ),
      companySize: readString(companyRaw.companySize ?? companyRaw.CompanySize),
      additionalContext: readString(
        companyRaw.additionalContext ?? companyRaw.AdditionalContext
      ),
    },
    project: {
      projectId: Number(projectId),
      projectName,
      projectNumber: readString(
        projectRaw.projectNumber ??
          projectRaw.ProjectNumber ??
          projectDetails?.projectNumber
      ),
      departmentName: readString(
        projectRaw.departmentName ?? projectRaw.DepartmentName
      ),
      projectDate: readString(projectRaw.projectDate ?? projectRaw.ProjectDate),
      details: readString(projectRaw.details ?? projectRaw.Details),
      assessmentBlueprintId:
        readNumber(
          projectRaw.assessmentBlueprintId ??
            projectRaw.AssessmentBlueprintId ??
            blueprintRaw.id ??
            blueprintRaw.Id ??
            flatBlueprintId
        ) ?? null,
    },
    assessmentBlueprint: {
      id: readNumber(blueprintRaw.id ?? blueprintRaw.Id ?? flatBlueprintId),
      title: readString(
        blueprintRaw.title ?? blueprintRaw.Title ?? flatBlueprintTitle
      ),
      description: readString(
        blueprintRaw.description ?? blueprintRaw.Description
      ),
      status: readString(blueprintRaw.status ?? blueprintRaw.Status),
      sourceTraxccelFrameworkId: readNumber(
        blueprintRaw.sourceTraxccelFrameworkId ??
          blueprintRaw.SourceTraxccelFrameworkId
      ),
      sourceTraxccelFrameworkTitle: readString(
        blueprintRaw.sourceTraxccelFrameworkTitle ??
          blueprintRaw.SourceTraxccelFrameworkTitle
      ),
      companyId:
        readNumber(blueprintRaw.companyId ?? blueprintRaw.CompanyId) ?? companyId,
      projectId:
        readNumber(blueprintRaw.projectId ?? blueprintRaw.ProjectId) ??
        Number(projectId),
      publishedAt:
        readString(blueprintRaw.publishedAt ?? blueprintRaw.PublishedAt) || null,
    },
    assessmentUnits: buildAssessmentUnits(selectedCapabilities),
  };
}
